import { connectClaudeSocket, disconnectClaudeSocket } from "../websockets/claudeSocket.js";

const REQUIRED = ["business", "offer", "goal", "audience", "cta", "language"];

function init() {
  const modal = document.getElementById("aiWebsiteModal");
  if (!modal) return;

  const form = document.getElementById("aiWebsiteForm");
  const generateBtn = document.getElementById("aiWebsiteGenerate");
  const progress = document.getElementById("aiWebsiteProgress");
  const progressText = document.getElementById("aiWebsiteProgressText");
  const errorBox = document.getElementById("aiWebsiteError");
  const campaignId = modal.dataset.campaignId;

  const field = (name) => form.querySelector(`[name="${name}"]`);
  const val = (name) => (field(name)?.value ?? "").trim();

  // Drei Wege, eine Wahl:
  //   url     → Inhalte und Farben einer Website auslesen, gebaut wird mit unseren Komponenten
  //   nachbau → Aufbau und Optik einer Vorlage nachbauen, Inhalte kommen aus dem Brief
  //   manual  → nur der Brief, die Sektionen frei gewählt
  // Jede Adresse geht nur im eigenen Modus mit. Ein liegengebliebenes Feld aus einem anderen
  // Modus löste sonst die falsche Strecke aus.
  const manualSec = document.getElementById("aiManualSection");
  const urlSec = document.getElementById("aiUrlSection");
  const vorlageGroup = document.getElementById("aiVorlageGruppe");
  const sectionsGroup = document.getElementById("aiSectionsGroup");

  const mode = () => form.querySelector('input[name="aiSourceMode"]:checked')?.value ?? "manual";
  const nachVorlage = () => mode() === "nachbau";

  const allRequiredFilled = () => {
    if (mode() === "url") return val("websiteUrl").length > 0;
    return (
      REQUIRED.every((n) => val(n).length > 0) && (!nachVorlage() || val("sourceUrl").length > 0)
    );
  };

  function refreshMode() {
    const m = mode();
    urlSec?.classList.toggle("d-none", m !== "url");
    vorlageGroup?.classList.toggle("d-none", m !== "nachbau");
    // Der Nachbau braucht den Brief genauso wie die manuelle Eingabe — die Vorlage liefert
    // nur die Form, Name und Inhalte kommen von hier.
    manualSec?.classList.toggle("d-none", m === "url");
    // Beim Nachbau bestimmt die Vorlage die Sektionen — dann nennt sie auch der Aufklapper nicht.
    sectionsGroup?.classList.toggle("d-none", m === "nachbau");
    document.getElementById("aiOptionalSektionen")?.classList.toggle("d-none", m === "nachbau");
    refreshGate();
  }
  form
    .querySelectorAll('input[name="aiSourceMode"]')
    .forEach((r) => r.addEventListener("change", refreshMode));

  // --- validation gate -----------------------------------------------------
  function refreshGate() {
    generateBtn.disabled = !allRequiredFilled();
  }
  form.addEventListener("input", refreshGate);
  form.addEventListener("change", refreshGate);
  refreshGate();

  // --- colour pickers <-> hex text mirror ----------------------------------
  form.querySelectorAll(".ai-color-picker").forEach((picker) => {
    const text = document.getElementById(picker.dataset.colorTarget);
    if (!text) return;
    picker.addEventListener("input", () => {
      text.value = picker.value;
    });
    text.addEventListener("input", () => {
      if (/^#[0-9a-fA-F]{6}$/.test(text.value.trim())) picker.value = text.value.trim();
    });
  });

  // --- pre-fill from the last saved brief ----------------------------------
  // So "Seite komplett neu generieren" starts from what the user last entered.
  function prefillFromBrief() {
    const raw = modal.dataset.aiBrief;
    if (!raw) return;
    let brief;
    try {
      brief = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!brief) return;

    [
      "business",
      "offer",
      "goal",
      "audience",
      "cta",
      "language",
      "brandStyle",
      "primaryColor",
      "secondaryColor",
      "accentColor",
      "tone",
      "additionalInstructions",
      "sourceUrl",
      "websiteUrl",
    ].forEach((name) => {
      const el = field(name);
      if (el && brief[name] != null && brief[name] !== "") el.value = brief[name];
    });

    if (Array.isArray(brief.sections)) {
      form.querySelectorAll(".ai-section").forEach((cb) => {
        cb.checked = brief.sections.includes(cb.value);
      });
    }

    form.querySelectorAll(".ai-color-picker").forEach((picker) => {
      const text = document.getElementById(picker.dataset.colorTarget);
      if (text && /^#[0-9a-fA-F]{6}$/.test(text.value.trim())) picker.value = text.value.trim();
    });

    const zuletzt = brief.sourceUrl ? "nachbau" : brief.websiteUrl ? "url" : "manual";
    const radio = form.querySelector(`input[name="aiSourceMode"][value="${zuletzt}"]`);
    if (radio) radio.checked = true;
  }
  prefillFromBrief();
  refreshMode();

  // --- AI disclosure toggle (persists immediately, independent of generation) --
  const disclosure = document.getElementById("aiDisclosureSwitch");
  if (disclosure) {
    disclosure.addEventListener("change", async () => {
      const token = document.querySelector('meta[name="_csrf"]');
      const header = document.querySelector('meta[name="_csrf_header"]');
      const headers = {};
      if (token && header) headers[header.content] = token.content;
      try {
        const resp = await fetch(
          `/campaigns/${campaignId}/ai-disclosure?enabled=${disclosure.checked}`,
          { method: "POST", headers },
        );
        if (!resp.ok) disclosure.checked = !disclosure.checked; // revert on failure
      } catch (e) {
        disclosure.checked = !disclosure.checked;
      }
    });
  }

  // --- refine / undo (AI mode) + editor-mode note --------------------------
  const refineSection = document.getElementById("aiRefineSection");
  const refineInput = document.getElementById("aiRefineInstruction");
  const refineBtn = document.getElementById("aiRefineSubmit");
  const regenerateToggle = document.getElementById("aiRegenerateToggle");
  const undoBtn = document.getElementById("aiUndoBtn");
  const editorModeNote = document.getElementById("aiEditorModeNote");

  // --- UI state ------------------------------------------------------------
  function setBusy(busy, text) {
    progress.classList.toggle("d-none", !busy);
    form.classList.toggle("d-none", busy || form.dataset.hidden === "true");
    if (refineSection) refineSection.classList.toggle("d-none", busy);
    if (editorModeNote) editorModeNote.classList.toggle("d-none", busy);
    if (text) progressText.textContent = text;
    generateBtn.disabled = busy || !allRequiredFilled();
    if (refineBtn) refineBtn.disabled = busy || !refineInput?.value.trim();
  }

  function showError(message) {
    errorBox.textContent = message;
    errorBox.classList.remove("d-none");
  }

  function buildBrief(taskId) {
    return {
      taskId,
      campaignId: Number(campaignId),
      websiteUrl: mode() === "url" ? val("websiteUrl") || null : null,
      business: val("business"),
      offer: val("offer"),
      goal: val("goal"),
      audience: val("audience"),
      cta: val("cta"),
      language: val("language"),
      // optional (null when empty)
      brandStyle: val("brandStyle") || null,
      primaryColor: val("primaryColor") || null,
      secondaryColor: val("secondaryColor") || null,
      accentColor: val("accentColor") || null,
      tone: val("tone") || null,
      sections: nachVorlage()
        ? []
        : Array.from(form.querySelectorAll(".ai-section:checked")).map((el) => el.value),
      additionalInstructions: val("additionalInstructions") || null,
      sourceUrl: nachVorlage() ? val("sourceUrl") || null : null,
    };
  }

  function csrfHeaders() {
    const token = document.querySelector('meta[name="_csrf"]');
    const header = document.querySelector('meta[name="_csrf_header"]');
    const headers = { "Content-Type": "application/json" };
    if (token && header) headers[header.content] = token.content;
    return headers;
  }

  async function runTask(taskId, url, body, busyText, onBadStatus) {
    errorBox.classList.add("d-none");
    setBusy(true, busyText);

    // Subscribe before dispatching so the final status isn't missed.
    connectClaudeSocket(
      taskId,
      (result) => {
        if (result.status === "SUCCESS") {
          progressText.textContent = "Fertig! Seite wird geladen…";
          window.location.reload();
        }
      },
      (err) => {
        disconnectClaudeSocket();
        setBusy(false);
        showError((err && err.error) || "Bei der Verarbeitung ist ein Fehler aufgetreten.");
      },
    );

    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: csrfHeaders(),
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        disconnectClaudeSocket();
        setBusy(false);
        showError(onBadStatus(resp.status));
      }
    } catch (e) {
      disconnectClaudeSocket();
      setBusy(false);
      showError("Netzwerkfehler. Bitte erneut versuchen.");
    }
  }

  // --- generate ------------------------------------------------------------
  generateBtn.addEventListener("click", () => {
    if (!allRequiredFilled()) {
      refreshGate();
      return;
    }
    const taskId = crypto.randomUUID();
    runTask(
      taskId,
      "/api/ai/generate/website",
      buildBrief(taskId),
      "Die KI erstellt Ihre Landing Page…",
      (status) =>
        status === 400
          ? "Bitte füllen Sie alle Pflichtfelder korrekt aus."
          : `Anfrage fehlgeschlagen (Status ${status}).`,
    );
  });

  // --- refine --------------------------------------------------------------
  // Track whether the intake form is intentionally hidden (a page already exists).
  form.dataset.hidden = form.classList.contains("d-none") ? "true" : "false";

  if (regenerateToggle) {
    regenerateToggle.addEventListener("click", (e) => {
      e.preventDefault();
      form.dataset.hidden = "false";
      form.classList.remove("d-none");
      regenerateToggle.classList.add("d-none");
    });
  }

  if (refineBtn && refineInput) {
    const refreshRefineGate = () => {
      refineBtn.disabled = !refineInput.value.trim();
    };
    refineInput.addEventListener("input", refreshRefineGate);
    refreshRefineGate();

    const submitRefine = () => {
      const instruction = refineInput.value.trim();
      if (!instruction) return;
      const taskId = crypto.randomUUID();
      runTask(
        taskId,
        "/api/ai/refine/website",
        { taskId, campaignId: Number(campaignId), instruction },
        "Die KI überarbeitet Ihre Seite…",
        (status) =>
          status === 409
            ? "Für diese Kampagne wurde noch keine Seite generiert."
            : status === 400
              ? "Bitte geben Sie eine gültige Anweisung ein."
              : `Anfrage fehlgeschlagen (Status ${status}).`,
      );
    };

    refineBtn.addEventListener("click", submitRefine);
    refineInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        submitRefine();
      }
    });
  }

  // --- undo (revert last AI change) — synchronous, no websocket ------------
  if (undoBtn) {
    undoBtn.addEventListener("click", async () => {
      errorBox.classList.add("d-none");
      setBusy(true, "Letzte KI-Änderung wird rückgängig gemacht…");
      try {
        const resp = await fetch(`/api/ai/undo/website?campaignId=${Number(campaignId)}`, {
          method: "POST",
          headers: csrfHeaders(),
        });
        if (resp.ok) {
          progressText.textContent = "Fertig! Seite wird geladen…";
          window.location.reload();
        } else {
          setBusy(false);
          showError(
            resp.status === 409
              ? "Es gibt keine KI-Änderung zum Rückgängigmachen."
              : `Rückgängig fehlgeschlagen (Status ${resp.status}).`,
          );
        }
      } catch (e) {
        setBusy(false);
        showError("Netzwerkfehler. Bitte erneut versuchen.");
      }
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
